import { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n';
import type { ReactionSummary } from '../../data/dto';
import { ALL_EMOJI, QUICK_EMOJI } from './emoji';

interface Props {
  summary: ReactionSummary[];
  /** The current user's chosen emoji, or null. */
  mine: string | null;
  /** Toggle a reaction (add / switch / remove). */
  onToggle: (emoji: string) => void;
  disabled?: boolean;
  /** Read-only (viewer role): show existing reactions but no add/toggle. */
  readOnly?: boolean;
}

/**
 * Reaction summary chips + an "add reaction" popover. WhatsApp-style: a user
 * has at most one reaction; tapping their current emoji removes it, tapping a
 * different one switches. The popover shows the top-5 quick row and a "+" that
 * expands to the full standard set.
 */
export function ReactionPicker({ summary, mine, onToggle, disabled = false, readOnly = false }: Props) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (emoji: string) => {
    onToggle(emoji);
    setOpen(false);
    setExpanded(false);
  };

  // Viewers: show the reaction counts as static chips, no add/toggle. If there
  // are none, render nothing at all.
  if (readOnly) {
    if (summary.length === 0) return null;
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {summary.map((r) => (
          <span
            key={r.emoji}
            className="inline-flex items-center gap-1 rounded-full border border-line bg-surface px-2 py-1 text-[13px] text-ink-2"
          >
            <span className="text-[15px] leading-none">{r.emoji}</span>
            <span className="font-semibold tabular-nums">{r.count}</span>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative flex flex-wrap items-center gap-1.5">
      {summary.map((r) => (
        <button
          key={r.emoji}
          type="button"
          disabled={disabled}
          onClick={() => onToggle(r.emoji)}
          className={[
            'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[13px] transition-colors',
            r.reacted_by_me
              ? 'border-accent/40 bg-accent-soft text-accent-ink'
              : 'border-line bg-surface text-ink-2 hover:bg-grey-soft',
          ].join(' ')}
          style={
            r.reacted_by_me
              ? { background: 'color-mix(in oklch, var(--color-accent) 12%, var(--color-bg))' }
              : undefined
          }
          aria-pressed={r.reacted_by_me}
        >
          <span className="text-[15px] leading-none">{r.emoji}</span>
          <span className="font-semibold tabular-nums">{r.count}</span>
        </button>
      ))}

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-label={t('engagement.addReaction', 'Add reaction')}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-line bg-surface text-ink-3 hover:bg-grey-soft"
      >
        <span className="text-[15px] leading-none">{mine ? '🙂' : '☺'}</span>
        <span className="-ml-0.5 text-[11px] font-bold leading-none">+</span>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-20 mt-1.5 w-[244px] rounded-2xl border border-line bg-surface p-2 shadow-lg">
          <div className="flex items-center justify-between gap-1">
            {QUICK_EMOJI.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => pick(e)}
                className={[
                  'flex h-9 w-9 items-center justify-center rounded-full text-[20px] transition-transform hover:scale-110',
                  mine === e ? 'bg-grey-soft' : '',
                ].join(' ')}
              >
                {e}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-label={t('engagement.moreEmoji', 'More emoji')}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-[15px] font-bold text-ink-3 hover:bg-grey-soft"
            >
              {expanded ? '×' : '+'}
            </button>
          </div>

          {expanded && (
            <div className="mt-2 grid max-h-44 grid-cols-8 gap-0.5 overflow-y-auto border-t border-line pt-2">
              {ALL_EMOJI.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => pick(e)}
                  className={[
                    'flex h-8 w-8 items-center justify-center rounded-lg text-[18px] hover:bg-grey-soft',
                    mine === e ? 'bg-grey-soft' : '',
                  ].join(' ')}
                >
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
